import "./style.css"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { ReactElement } from "react"
import { Provider } from "react-redux"
import { ThemeProvider } from "styled-components"

import { Colors } from "./themes/palette"
import { ShelfTheme } from "./themes/base"
import store from "./reducers/store"
import EventTypes from "./pages/event-types/EventTypes"
import PurchasePlan from "pages/purchase-plans/PurchasePlan"
import Login from "./pages/login/Login"
import Products from "./pages/products/Products"
import PrivateRoute from "./components/common/auth/PrivateRoute"
import { QueryClient, QueryClientProvider } from "react-query"
import SidebarRoutes from "components/EvtKit/sidebar-menu/Sidebar"
import Accounts from "pages/accounts/Accounts"
import Invoice from "pages/invoice/Invoice"
import ApiMapping from "pages/api-mappings/ApiMappings"
import ApiMappingEdit from "pages/api-mappings/ApiMappingsEdit"
import PurchasePlanCreation from "pages/purchase-plans/PurchasePlanCreation"
import PurchasePlanUpdate from "pages/purchase-plans/PurchasePlanUpdate"
import { BeatThemeProvider } from "components/BeatTheme/BeatThemeProvider"
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles"
import Home from "pages/home/Home"
import Webhook from "pages/webhooks/Webhook"
import WebhookForm from "pages/webhooks/WebhookForm"
import ProductCreation from "pages/products/components/v2/ProductCreation"
import ProductUpdate from "pages/products/components/v2/ProductUpdate"
import { Profile } from "pages/profile/Profile"
import PublicRoute from "components/common/auth/PublicRoute"
import Metrics from "pages/metrics/Metrics"

const theme = {
  palette: Colors
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 1000 * 60 * 3 // 3 minutes
    }
  }
})

export const App = (): ReactElement => {
  return (
    <>
      <BeatThemeProvider
        logo="https://media.glassdoor.com/sqll/3075154/evt-squarelogo-1572037191297.png"
        theme={ShelfTheme}
      >
        <BrowserRouter>
          <ThemeProvider theme={theme}>
            <MuiThemeProvider theme={createTheme()}>
              <Provider store={store}>
                <QueryClientProvider client={queryClient}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/login" replace />} />
                    <Route path="login" element={<PublicRoute outlet={<Login />} />} />

                    <Route element={<SidebarRoutes />}>
                      <Route path="/home" element={<PrivateRoute outlet={<Home />} />} />
                      <Route path="/products" element={<PrivateRoute outlet={<Products />} />} />
                      <Route path="/products/create" element={<PrivateRoute outlet={<ProductCreation />} />} />
                      <Route
                        path="/products/update?/:productParamsId"
                        element={<PrivateRoute outlet={<ProductUpdate />} />}
                      />
                      <Route path="/purchase-plans" element={<PrivateRoute outlet={<PurchasePlan />} />} />
                      <Route
                        path="/purchase-plans/create"
                        element={<PrivateRoute outlet={<PurchasePlanCreation />} />}
                      />
                      <Route
                        path="/purchase-plans/update?/:purchasePlanId"
                        element={<PrivateRoute outlet={<PurchasePlanUpdate />} />}
                      />
                      <Route path="/event-types" element={<PrivateRoute outlet={<EventTypes />} />} />
                      <Route path="/clients" element={<PrivateRoute outlet={<Accounts />} />} />
                      <Route path="/invoices" element={<PrivateRoute outlet={<Invoice />} />} />
                      <Route path="/api-mappings" element={<PrivateRoute outlet={<ApiMapping />} />} />
                      <Route path="/api-mappings/:id" element={<PrivateRoute outlet={<ApiMappingEdit />} />} />
                      <Route path="/webhooks" element={<PrivateRoute outlet={<Webhook />} />} />
                      <Route path="/webhooks/:id" element={<PrivateRoute outlet={<WebhookForm />} />} />
                      <Route path="/metrics" element={<PrivateRoute outlet={<Metrics />} />} />
                      <Route path="/profile" element={<PrivateRoute outlet={<Profile />} />} />
                    </Route>
                  </Routes>
                </QueryClientProvider>
              </Provider>
            </MuiThemeProvider>
          </ThemeProvider>
        </BrowserRouter>
      </BeatThemeProvider>
    </>
  )
}
